import React, { useEffect, useState, useRef, Children } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';
import { Card } from '../../ui/Card';
import { SearchIcon, PlusIcon, FilterIcon, MoreHorizontalIcon, DownloadIcon, PencilIcon, BanIcon, CheckCircleIcon, StoreIcon, BoxIcon, PackageIcon, TagIcon, MailIcon, UserPlusIcon, UsersIcon, XIcon, GridIcon, ClockIcon, TrendingUpIcon, FileTextIcon, NetworkIcon } from 'lucide-react';
import { exhibitors, Exhibitor } from '../../../data/exhibitors';
import { LinkingStatusBadge } from '../shared/LinkingStatusBadge';
import { ExhibitorProfilePage } from './ExhibitorProfilePage';
interface ExhibitorsManagementProps {
  onBack: () => void;
  onExhibitorSelect?: (exhibitor: Exhibitor | null) => void;
  onRegisterExitHandler?: (handler: () => void) => void;
}
type StandType = 'Space Only' | 'Shell Scheme' | 'Show Ready' | 'Pod' | 'Brand Only';
const STAND_TYPES: {
  type: StandType;
  icon: React.ReactNode;
  description: string;
}[] = [{
  type: 'Space Only',
  icon: <BoxIcon className="w-5 h-5" data-id="element-1291" />,
  description: 'Raw space with no infrastructure'
}, {
  type: 'Shell Scheme',
  icon: <PackageIcon className="w-5 h-5" data-id="element-1292" />,
  description: 'Basic booth with walls and carpet'
}, {
  type: 'Show Ready',
  icon: <StoreIcon className="w-5 h-5" data-id="element-1293" />,
  description: 'Fully built and furnished booth'
}, {
  type: 'Pod',
  icon: <GridIcon className="w-5 h-5" data-id="element-1294" />,
  description: 'Small modular exhibition space'
}, {
  type: 'Brand Only',
  icon: <TagIcon className="w-5 h-5" data-id="element-1295" />,
  description: 'Branding presence without booth'
}];
// Mock user data for exhibitors
const mockExhibitorUsers: Record<string, {
  id: string;
  name: string;
  email: string;
  role: string;
  isPrimary: boolean;
}[]> = {
  '1': [{
    id: 'u1',
    name: 'John Doe',
    email: 'john@orbit4.org',
    role: 'Sales Manager',
    isPrimary: true
  }, {
    id: 'u2',
    name: 'Jane Smith',
    email: 'jane@orbit4.org',
    role: 'Marketing Director',
    isPrimary: false
  }],
  '2': [{
    id: 'u3',
    name: 'Mike Johnson',
    email: 'mike@farahsecrets.com',
    role: 'CEO',
    isPrimary: true
  }]
};
// Mock progress data
const mockProgressData: Record<string, {
  brandProfile: number;
  exhibitorPortal: number;
  lastLogin: string;
}> = {
  '1': {
    brandProfile: 85,
    exhibitorPortal: 60,
    lastLogin: '2 hours ago'
  },
  '2': {
    brandProfile: 45,
    exhibitorPortal: 30,
    lastLogin: '1 day ago'
  },
  '3': {
    brandProfile: 90,
    exhibitorPortal: 75,
    lastLogin: '3 hours ago'
  }
};
// Mock existing app users for lookup
const mockAppUsers = [{
  id: 'app-u1',
  name: 'Sarah Wilson',
  email: 'sarah@example.com',
  role: 'Marketing Director'
}, {
  id: 'app-u2',
  name: 'Tom Brown',
  email: 'tom@example.com',
  role: 'Sales Manager'
}, {
  id: 'app-u3',
  name: 'Lisa Chen',
  email: 'lisa@example.com',
  role: 'CEO'
}, {
  id: 'app-u4',
  name: 'David Lee',
  email: 'david@example.com',
  role: 'Operations Manager'
}];
interface InvitedContact {
  id: string;
  name: string;
  email: string;
  role: string;
  isExisting: boolean;
}
export function ExhibitorsManagement({
  onBack,
  onExhibitorSelect,
  onRegisterExitHandler
}: ExhibitorsManagementProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);
  const [showInviteUserModal, setShowInviteUserModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showFiltersDropdown, setShowFiltersDropdown] = useState(false);
  // Filter states
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [selectedStandTypes, setSelectedStandTypes] = useState<StandType[]>([]);
  const [showParents, setShowParents] = useState(false);
  const [showChildren, setShowChildren] = useState(false);
  const [showStandalone, setShowStandalone] = useState(false);
  const [selectedHeadquarters, setSelectedHeadquarters] = useState<string[]>([]);
  const [profileCompletionFilter, setProfileCompletionFilter] = useState<'all' | 'complete' | 'incomplete'>('all');
  const [onboardingCompletionFilter, setOnboardingCompletionFilter] = useState<'all' | 'complete' | 'incomplete'>('all');
  const [exhibitorToUpdate, setExhibitorToUpdate] = useState<Exhibitor | null>(null);
  const [selectedExhibitorForInvite, setSelectedExhibitorForInvite] = useState<Exhibitor | null>(null);
  const [selectedExhibitorForTeam, setSelectedExhibitorForTeam] = useState<Exhibitor | null>(null);
  const [selectedExhibitorForProfile, setSelectedExhibitorForProfile] = useState<Exhibitor | null>(null);
  // Add form state (keeping existing state variables)
  const [addName, setAddName] = useState('');
  const [addStandNumber, setAddStandNumber] = useState('');
  const [addStandType, setAddStandType] = useState<StandType>('Space Only');
  const [addSelectedEvents, setAddSelectedEvents] = useState<string[]>([]);
  const [addParentExhibitor, setAddParentExhibitor] = useState<string>('');
  const [addParentSearchTerm, setAddParentSearchTerm] = useState('');
  const [addShowParentDropdown, setAddShowParentDropdown] = useState(false);
  const [addInvitedContacts, setAddInvitedContacts] = useState<InvitedContact[]>([]);
  const [addShowUserLookup, setAddShowUserLookup] = useState(false);
  const [addUserSearchTerm, setAddUserSearchTerm] = useState('');
  const [addNewContactName, setAddNewContactName] = useState('');
  const [addNewContactEmail, setAddNewContactEmail] = useState('');
  const [addNewContactRole, setAddNewContactRole] = useState('');
  // Update form state
  const [updateName, setUpdateName] = useState('');
  const [updateStandNumber, setUpdateStandNumber] = useState('');
  const [updateStandType, setUpdateStandType] = useState<StandType>('Space Only');
  const [updateSelectedEvents, setUpdateSelectedEvents] = useState<string[]>([]);
  const [updateParentExhibitor, setUpdateParentExhibitor] = useState<string>('');
  const [updateParentSearchTerm, setUpdateParentSearchTerm] = useState('');
  const [updateShowParentDropdown, setUpdateShowParentDropdown] = useState(false);
  // Invite user form state
  const [inviteUserName, setInviteUserName] = useState('');
  const [inviteUserEmail, setInviteUserEmail] = useState('');
  const [inviteUserRole, setInviteUserRole] = useState('');
  const [inviteAsPrimary, setInviteAsPrimary] = useState(false);
  const initializedRef = useRef(false);
  const [exhibitorsList, setExhibitorsList] = useState<Exhibitor[]>([]);
  const filtersRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!initializedRef.current) {
      setExhibitorsList(exhibitors);
      initializedRef.current = true;
    }
  }, []);
  // Register exit handler with parent
  useEffect(() => {
    if (onRegisterExitHandler) {
      onRegisterExitHandler(() => {
        setSelectedExhibitorForProfile(null);
        if (onExhibitorSelect) {
          onExhibitorSelect(null);
        }
      });
    }
  }, [onRegisterExitHandler, onExhibitorSelect]);
  // Close filters dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(event.target as Node)) {
        setShowFiltersDropdown(false);
      }
    };
    if (showFiltersDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showFiltersDropdown]);
  const resultsPerPage = 100;
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const availableEvents = [{
    id: 'DAI',
    name: 'Dubai Active Industry'
  }, {
    id: 'DAS',
    name: 'Dubai Active Show'
  }, {
    id: 'DMS',
    name: 'Dubai Muscle Show'
  }];
  // Helper functions - MOVED BEFORE FILTERED EXHIBITORS
  const getProgressData = (exhibitorId: string) => {
    return mockProgressData[exhibitorId] || {
      brandProfile: 0,
      exhibitorPortal: 0,
      lastLogin: 'Never'
    };
  };
  const isParentExhibitor = (exhibitorId: string) => {
    return exhibitorsList.some(ex => ex.parentExhibitorId === exhibitorId);
  };
  const getChildCount = (exhibitorId: string) => {
    return exhibitorsList.filter(ex => ex.parentExhibitorId === exhibitorId).length;
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        setShowActionsMenu(null);
      }
    };
    if (showActionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showActionsMenu]);
  const toggleEventFilter = (eventId: string) => {
    setSelectedEvents(prev => prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]);
  };
  const toggleStandTypeFilter = (standType: StandType) => {
    setSelectedStandTypes(prev => prev.includes(standType) ? prev.filter(type => type !== standType) : [...prev, standType]);
  };
  const toggleAddEventSelection = (eventId: string) => {
    setAddSelectedEvents(prev => prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]);
  };
  const toggleUpdateEventSelection = (eventId: string) => {
    setUpdateSelectedEvents(prev => prev.includes(eventId) ? prev.filter(id => id !== eventId) : [...prev, eventId]);
  };
  const clearFilters = () => {
    setSelectedEvents([]);
    setSelectedStandTypes([]);
    setShowParents(false);
    setShowChildren(false);
    setShowStandalone(false);
    setSelectedHeadquarters([]);
    setProfileCompletionFilter('all');
    setOnboardingCompletionFilter('all');
  };
  const activeFiltersCount = selectedEvents.length + selectedStandTypes.length + selectedHeadquarters.length + (showParents ? 1 : 0) + (showChildren ? 1 : 0) + (showStandalone ? 1 : 0) + (profileCompletionFilter !== 'all' ? 1 : 0) + (onboardingCompletionFilter !== 'all' ? 1 : 0);
  // Get unique headquarters for filter
  const uniqueHeadquarters = Array.from(new Set(exhibitorsList.map(ex => ex.headquarters).filter(Boolean))).sort() as string[];
  const toggleHeadquartersFilter = (hq: string) => {
    setSelectedHeadquarters(prev => prev.includes(hq) ? prev.filter(h => h !== hq) : [...prev, hq]);
  };
  const filteredExhibitors = exhibitorsList.filter(ex => {
    // Search filter - search both name and stand number
    const matchesSearch = ex.name.toLowerCase().includes(searchTerm.toLowerCase()) || ex.standNumber.toLowerCase().includes(searchTerm.toLowerCase());
    // Event filter
    const matchesEvent = selectedEvents.length === 0 || ex.events.some(event => selectedEvents.includes(event));
    // Stand type filter
    const matchesStandType = selectedStandTypes.length === 0 || selectedStandTypes.includes(ex.standType);
    // Headquarters filter
    const matchesHeadquarters = selectedHeadquarters.length === 0 || ex.headquarters && selectedHeadquarters.includes(ex.headquarters);
    // Relationship type filter - NOW SUPPORTS MULTIPLE SELECTIONS
    let matchesRelationship = true;
    if (showParents || showChildren || showStandalone) {
      const isParent = isParentExhibitor(ex.id);
      const isChild = !!ex.parentExhibitorId;
      const isStandalone = !ex.parentExhibitorId && !isParent;
      matchesRelationship = showParents && isParent || showChildren && isChild || showStandalone && isStandalone;
    }
    // Profile completion filter
    let matchesCompletion = true;
    if (profileCompletionFilter === 'complete') {
      const progressData = getProgressData(ex.id);
      matchesCompletion = progressData.brandProfile === 100;
    } else if (profileCompletionFilter === 'incomplete') {
      const progressData = getProgressData(ex.id);
      matchesCompletion = progressData.brandProfile < 100;
    }
    // Onboarding completion filter
    let matchesOnboarding = true;
    if (onboardingCompletionFilter === 'complete') {
      const progressData = getProgressData(ex.id);
      matchesOnboarding = progressData.exhibitorPortal === 100;
    } else if (onboardingCompletionFilter === 'incomplete') {
      const progressData = getProgressData(ex.id);
      matchesOnboarding = progressData.exhibitorPortal < 100;
    }
    return matchesSearch && matchesEvent && matchesStandType && matchesHeadquarters && matchesRelationship && matchesCompletion && matchesOnboarding;
  }).sort((a, b) => a.name.localeCompare(b.name));
  const totalPages = Math.ceil(filteredExhibitors.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const currentExhibitors = filteredExhibitors.slice(startIndex, endIndex);
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  // REMOVED DUPLICATE HELPER FUNCTIONS FROM HERE
  // (getProgressData, isParentExhibitor, getChildCount are now defined above)
  const handleAddModalOpen = () => {
    setAddName('');
    setAddStandNumber('');
    setAddStandType('Space Only');
    setAddSelectedEvents([]);
    setAddParentExhibitor('');
    setAddParentSearchTerm('');
    setAddShowParentDropdown(false);
    setAddInvitedContacts([]);
    setAddShowUserLookup(false);
    setAddUserSearchTerm('');
    setAddNewContactName('');
    setAddNewContactEmail('');
    setAddNewContactRole('');
    setShowAddModal(true);
  };
  const handleAddExistingUser = (user: (typeof mockAppUsers)[0]) => {
    const newContact: InvitedContact = {
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      isExisting: true
    };
    setAddInvitedContacts(prev => [...prev, newContact]);
    setAddUserSearchTerm('');
    setAddShowUserLookup(false);
  };
  const handleAddNewContact = () => {
    if (!addNewContactName || !addNewContactEmail) return;
    const newContact: InvitedContact = {
      id: `new-${Date.now()}`,
      name: addNewContactName,
      email: addNewContactEmail,
      role: addNewContactRole,
      isExisting: false
    };
    setAddInvitedContacts(prev => [...prev, newContact]);
    setAddNewContactName('');
    setAddNewContactEmail('');
    setAddNewContactRole('');
  };
  const handleRemoveContact = (contactId: string) => {
    setAddInvitedContacts(prev => prev.filter(c => c.id !== contactId));
  };
  const filteredAppUsers = mockAppUsers.filter(user => (user.name.toLowerCase().includes(addUserSearchTerm.toLowerCase()) || user.email.toLowerCase().includes(addUserSearchTerm.toLowerCase())) && !addInvitedContacts.some(c => c.id === user.id));
  // Filter parent exhibitors for Add modal
  const filteredAddParentExhibitors = exhibitorsList.filter(ex => ex.name.toLowerCase().includes(addParentSearchTerm.toLowerCase()) || ex.standNumber.toLowerCase().includes(addParentSearchTerm.toLowerCase()));
  // Filter parent exhibitors for Update modal
  const filteredUpdateParentExhibitors = exhibitorsList.filter(ex => ex.id !== exhibitorToUpdate?.id && (ex.name.toLowerCase().includes(updateParentSearchTerm.toLowerCase()) || ex.standNumber.toLowerCase().includes(updateParentSearchTerm.toLowerCase())));
  const getParentExhibitorName = (parentId: string) => {
    const parent = exhibitorsList.find(ex => ex.id === parentId);
    return parent ? `${parent.name} (${parent.standNumber})` : '';
  };
  const handleSelectAddParent = (exhibitorId: string) => {
    setAddParentExhibitor(exhibitorId);
    setAddParentSearchTerm(getParentExhibitorName(exhibitorId));
    setAddShowParentDropdown(false);
  };
  const handleSelectUpdateParent = (exhibitorId: string) => {
    setUpdateParentExhibitor(exhibitorId);
    setUpdateParentSearchTerm(getParentExhibitorName(exhibitorId));
    setUpdateShowParentDropdown(false);
  };
  const handleClearAddParent = () => {
    setAddParentExhibitor('');
    setAddParentSearchTerm('');
  };
  const handleClearUpdateParent = () => {
    setUpdateParentExhibitor('');
    setUpdateParentSearchTerm('');
  };
  // Check if stand number should be disabled
  const isStandNumberDisabled = (standType: StandType) => standType === 'Brand Only';
  const handleAddExhibitor = () => {
    const newId = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newExhibitor: Exhibitor = {
      id: newId,
      name: addName,
      logo: '',
      industries: [],
      headquarters: null,
      events: addSelectedEvents,
      email: null,
      socials: null,
      sponsor: null,
      standNumber: isStandNumberDisabled(addStandType) ? 'N/A' : addStandNumber,
      standType: addStandType,
      website: '',
      description: '',
      isFavorite: false,
      profileStatus: addInvitedContacts.length > 0 ? 'pending-setup' : 'basic'
    };
    setExhibitorsList(prevList => [...prevList, newExhibitor]);
    if (addInvitedContacts.length > 0) {
      console.log('Inviting contacts:', {
        exhibitorId: newId,
        contacts: addInvitedContacts
      });
    }
    setShowAddModal(false);
  };
  const handleUpdateExhibitor = (exhibitor: Exhibitor) => {
    setExhibitorToUpdate(exhibitor);
    setUpdateName(exhibitor.name);
    setUpdateStandNumber(exhibitor.standNumber);
    setUpdateStandType(exhibitor.standType);
    setUpdateSelectedEvents(exhibitor.events);
    setUpdateParentExhibitor('');
    setUpdateParentSearchTerm('');
    setUpdateShowParentDropdown(false);
    setShowUpdateModal(true);
    setShowActionsMenu(null);
  };
  const handleSaveUpdate = () => {
    if (!exhibitorToUpdate) return;
    setExhibitorsList(prevList => prevList.map(ex => ex.id === exhibitorToUpdate.id ? {
      ...ex,
      name: updateName,
      standNumber: isStandNumberDisabled(updateStandType) ? 'N/A' : updateStandNumber,
      standType: updateStandType,
      events: updateSelectedEvents
    } : ex));
    setShowUpdateModal(false);
    setExhibitorToUpdate(null);
  };
  const handleSuspendExhibitor = (exhibitorId: string) => {
    setShowActionsMenu(null);
    alert('Suspend functionality would be implemented here');
  };
  const handleOpenInviteUser = (exhibitor: Exhibitor) => {
    setSelectedExhibitorForInvite(exhibitor);
    setInviteUserName('');
    setInviteUserEmail('');
    setInviteUserRole('');
    setInviteAsPrimary(!mockExhibitorUsers[exhibitor.id] || mockExhibitorUsers[exhibitor.id].length === 0);
    setShowInviteUserModal(true);
    setShowActionsMenu(null);
  };
  const handleInviteUser = () => {
    if (!selectedExhibitorForInvite) return;
    console.log('Inviting user to exhibitor:', {
      exhibitorId: selectedExhibitorForInvite.id,
      name: inviteUserName,
      email: inviteUserEmail,
      role: inviteUserRole,
      isPrimary: inviteAsPrimary
    });
    setShowInviteUserModal(false);
    setSelectedExhibitorForInvite(null);
  };
  const handleViewTeam = (exhibitor: Exhibitor) => {
    setSelectedExhibitorForTeam(exhibitor);
    setShowTeamModal(true);
    setShowActionsMenu(null);
  };
  const handleRowClick = (exhibitor: Exhibitor) => {
    setSelectedExhibitorForProfile(exhibitor);
    if (onExhibitorSelect) {
      onExhibitorSelect(exhibitor);
    }
  };
  const handleSaveProfile = (updatedData: Partial<Exhibitor>) => {
    if (!selectedExhibitorForProfile) return;
    setExhibitorsList(prevList => prevList.map(ex => ex.id === selectedExhibitorForProfile.id ? {
      ...ex,
      ...updatedData
    } : ex));
    setSelectedExhibitorForProfile(null);
    if (onExhibitorSelect) {
      onExhibitorSelect(null);
    }
  };
  const getExhibitorUsers = (exhibitorId: string) => {
    return mockExhibitorUsers[exhibitorId] || [];
  };
  // If viewing profile, show profile page
  if (selectedExhibitorForProfile) {
    return <ExhibitorProfilePage exhibitor={selectedExhibitorForProfile} allExhibitors={exhibitorsList} onNavigateToExhibitor={exhibitorId => {
      const exhibitor = exhibitorsList.find(ex => ex.id === exhibitorId);
      if (exhibitor) {
        setSelectedExhibitorForProfile(exhibitor);
        if (onExhibitorSelect) {
          onExhibitorSelect(exhibitor);
        }
      }
    }} onBack={() => {
      setSelectedExhibitorForProfile(null);
      if (onExhibitorSelect) {
        onExhibitorSelect(null);
      }
    }} onSave={handleSaveProfile} data-id="element-1296" />;
  }
  return <div className="space-y-6" data-id="element-1297">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-1298">
        <div data-id="element-1299">
          <h1 className="text-2xl font-bold text-gray-900 font-inter" data-id="element-1300">
            Exhibitors
          </h1>
          <p className="text-gray-500 font-inter mt-1" data-id="element-1301">
            Manage exhibitors and their team members across all sub-events
          </p>
        </div>
        <div className="flex gap-3" data-id="element-1302">
          <Button variant="outline" data-id="element-1303">
            <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1304" />
            Export
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)} data-id="element-1305">
            <PlusIcon className="w-4 h-4 mr-2" data-id="element-1306" />
            Add Exhibitor
          </Button>
        </div>
      </div>

      {/* Enhanced Filters Bar */}
      <div className="bg-white p-4 rounded-lg border border-gray-200" data-id="element-1307">
        <div className="flex gap-4 items-start" data-id="element-1308">
          {/* Search */}
          <div className="relative flex-1 max-w-md" data-id="element-1309">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1310" />
            <input type="text" placeholder="Search by exhibitor name or stand number..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-inter text-sm" value={searchTerm} onChange={e => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }} data-id="element-1311" />
          </div>

          {/* Quick Filters */}
          <div className="flex gap-2 flex-wrap" data-id="element-1312">
            <button onClick={() => setShowParents(!showParents)} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${showParents ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1313">
              <NetworkIcon className="w-4 h-4 inline mr-1.5" data-id="element-1314" />
              Parents
            </button>

            <button onClick={() => setShowChildren(!showChildren)} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${showChildren ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1315">
              <span className="text-xs mr-1.5" data-id="element-1316">↳</span>
              Children
            </button>

            <button onClick={() => setProfileCompletionFilter(profileCompletionFilter === 'incomplete' ? 'all' : 'incomplete')} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${profileCompletionFilter === 'incomplete' ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1317">
              <TrendingUpIcon className="w-4 h-4 inline mr-1.5" data-id="element-1318" />
              Incomplete Profiles
            </button>

            <button onClick={() => setOnboardingCompletionFilter(onboardingCompletionFilter === 'incomplete' ? 'all' : 'incomplete')} className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${onboardingCompletionFilter === 'incomplete' ? 'bg-purple-50 border-purple-200 text-purple-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-1319">
              <UserPlusIcon className="w-4 h-4 inline mr-1.5" data-id="element-1320" />
              Incomplete Onboarding
            </button>
          </div>

          {/* Advanced Filters Dropdown */}
          <div className="relative" ref={filtersRef} data-id="element-1321">
            <Button variant="outline" size="sm" onClick={() => setShowFiltersDropdown(!showFiltersDropdown)} data-id="element-1322">
              <FilterIcon className="w-4 h-4 mr-2" data-id="element-1323" />
              More Filters
              {activeFiltersCount > 0 && <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full" data-id="element-1324">
                  {activeFiltersCount}
                </span>}
            </Button>

            {showFiltersDropdown && <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] overflow-y-auto" data-id="element-1325">
                <div className="p-4" data-id="element-1326">
                  <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2 border-b border-gray-100" data-id="element-1327">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-1328">
                      Advanced Filters
                    </h3>
                    {activeFiltersCount > 0 && <button onClick={clearFilters} className="text-xs text-blue-600 hover:text-blue-700" data-id="element-1329">
                        Clear all
                      </button>}
                  </div>

                  {/* Event Filter */}
                  <div className="mb-4" data-id="element-1330">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-1331">
                      Assigned Events
                    </label>
                    <div className="space-y-2" data-id="element-1332">
                      {availableEvents.map(event => <label key={event.id} className="flex items-center gap-2 cursor-pointer" data-id="element-1333">
                          <input type="checkbox" checked={selectedEvents.includes(event.id)} onChange={() => toggleEventFilter(event.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1334" />
                          <span className="text-sm text-gray-700" data-id="element-1335">
                            {event.name}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  {/* Stand Type Filter */}
                  <div className="mb-4" data-id="element-1336">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-1337">
                      Stand Type
                    </label>
                    <div className="space-y-2" data-id="element-1338">
                      {STAND_TYPES.map(standType => <label key={standType.type} className="flex items-center gap-2 cursor-pointer" data-id="element-1339">
                          <input type="checkbox" checked={selectedStandTypes.includes(standType.type)} onChange={() => toggleStandTypeFilter(standType.type)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1340" />
                          <span className="text-sm text-gray-700" data-id="element-1341">
                            {standType.type}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  {/* Headquarters Filter */}
                  <div className="mb-4" data-id="element-1342">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-1343">
                      Headquarters
                    </label>
                    <div className="space-y-2 max-h-40 overflow-y-auto" data-id="element-1344">
                      {uniqueHeadquarters.map(hq => <label key={hq} className="flex items-center gap-2 cursor-pointer" data-id="element-1345">
                          <input type="checkbox" checked={selectedHeadquarters.includes(hq)} onChange={() => toggleHeadquartersFilter(hq)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1346" />
                          <span className="text-sm text-gray-700" data-id="element-1347">{hq}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Relationship Type Filter - NOW CHECKBOXES */}
                  <div className="mb-4" data-id="element-1348">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-1349">
                      Brand Relationships
                    </label>
                    <div className="space-y-2" data-id="element-1350">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1351">
                        <input type="checkbox" checked={showParents} onChange={() => setShowParents(!showParents)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1352" />
                        <span className="text-sm text-gray-700" data-id="element-1353">Parents</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1354">
                        <input type="checkbox" checked={showChildren} onChange={() => setShowChildren(!showChildren)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1355" />
                        <span className="text-sm text-gray-700" data-id="element-1356">Children</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1357">
                        <input type="checkbox" checked={showStandalone} onChange={() => setShowStandalone(!showStandalone)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1358" />
                        <span className="text-sm text-gray-700" data-id="element-1359">
                          Standalone Brands
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Profile Completion Filter */}
                  <div className="mb-4" data-id="element-1360">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-1361">
                      Brand Profile Completion
                    </label>
                    <div className="space-y-2" data-id="element-1362">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1363">
                        <input type="radio" checked={profileCompletionFilter === 'all'} onChange={() => setProfileCompletionFilter('all')} className="text-blue-600 focus:ring-blue-500" data-id="element-1364" />
                        <span className="text-sm text-gray-700" data-id="element-1365">
                          All Profiles
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1366">
                        <input type="radio" checked={profileCompletionFilter === 'complete'} onChange={() => setProfileCompletionFilter('complete')} className="text-blue-600 focus:ring-blue-500" data-id="element-1367" />
                        <span className="text-sm text-gray-700" data-id="element-1368">
                          Complete (100%)
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1369">
                        <input type="radio" checked={profileCompletionFilter === 'incomplete'} onChange={() => setProfileCompletionFilter('incomplete')} className="text-blue-600 focus:ring-blue-500" data-id="element-1370" />
                        <span className="text-sm text-gray-700" data-id="element-1371">
                          Incomplete (&lt;100%)
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Onboarding Completion Filter - NEW */}
                  <div data-id="element-1372">
                    <label className="text-xs font-medium text-gray-700 mb-2 block" data-id="element-1373">
                      Exhibitor Portal Onboarding
                    </label>
                    <div className="space-y-2" data-id="element-1374">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1375">
                        <input type="radio" checked={onboardingCompletionFilter === 'all'} onChange={() => setOnboardingCompletionFilter('all')} className="text-blue-600 focus:ring-blue-500" data-id="element-1376" />
                        <span className="text-sm text-gray-700" data-id="element-1377">All</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1378">
                        <input type="radio" checked={onboardingCompletionFilter === 'complete'} onChange={() => setOnboardingCompletionFilter('complete')} className="text-blue-600 focus:ring-blue-500" data-id="element-1379" />
                        <span className="text-sm text-gray-700" data-id="element-1380">
                          Complete (100%)
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-1381">
                        <input type="radio" checked={onboardingCompletionFilter === 'incomplete'} onChange={() => setOnboardingCompletionFilter('incomplete')} className="text-blue-600 focus:ring-blue-500" data-id="element-1382" />
                        <span className="text-sm text-gray-700" data-id="element-1383">
                          Incomplete (&lt;100%)
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>

        {/* Active Filters Summary */}
        {activeFiltersCount > 0 && <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2 flex-wrap" data-id="element-1384">
            <span className="text-xs font-medium text-gray-500" data-id="element-1385">
              Active filters:
            </span>
            {showParents && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded flex items-center gap-1" data-id="element-1386">
                Parents
                <button onClick={() => setShowParents(false)} className="hover:text-blue-900" data-id="element-1387">
                  <XIcon className="w-3 h-3" data-id="element-1388" />
                </button>
              </span>}
            {showChildren && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded flex items-center gap-1" data-id="element-1389">
                Children
                <button onClick={() => setShowChildren(false)} className="hover:text-blue-900" data-id="element-1390">
                  <XIcon className="w-3 h-3" data-id="element-1391" />
                </button>
              </span>}
            {showStandalone && <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded flex items-center gap-1" data-id="element-1392">
                Standalone
                <button onClick={() => setShowStandalone(false)} className="hover:text-blue-900" data-id="element-1393">
                  <XIcon className="w-3 h-3" data-id="element-1394" />
                </button>
              </span>}
            {profileCompletionFilter !== 'all' && <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded flex items-center gap-1" data-id="element-1395">
                Profile:{' '}
                {profileCompletionFilter === 'complete' ? 'Complete' : 'Incomplete'}
                <button onClick={() => setProfileCompletionFilter('all')} className="hover:text-orange-900" data-id="element-1396">
                  <XIcon className="w-3 h-3" data-id="element-1397" />
                </button>
              </span>}
            {onboardingCompletionFilter !== 'all' && <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded flex items-center gap-1" data-id="element-1398">
                Onboarding:{' '}
                {onboardingCompletionFilter === 'complete' ? 'Complete' : 'Incomplete'}
                <button onClick={() => setOnboardingCompletionFilter('all')} className="hover:text-purple-900" data-id="element-1399">
                  <XIcon className="w-3 h-3" data-id="element-1400" />
                </button>
              </span>}
            {selectedEvents.map(eventId => <span key={eventId} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1" data-id="element-1401">
                {eventId}
                <button onClick={() => toggleEventFilter(eventId)} className="hover:text-gray-900" data-id="element-1402">
                  <XIcon className="w-3 h-3" data-id="element-1403" />
                </button>
              </span>)}
            {selectedStandTypes.map(type => <span key={type} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1" data-id="element-1404">
                {type}
                <button onClick={() => toggleStandTypeFilter(type)} className="hover:text-gray-900" data-id="element-1405">
                  <XIcon className="w-3 h-3" data-id="element-1406" />
                </button>
              </span>)}
            {selectedHeadquarters.map(hq => <span key={hq} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded flex items-center gap-1" data-id="element-1407">
                {hq}
                <button onClick={() => toggleHeadquartersFilter(hq)} className="hover:text-gray-900" data-id="element-1408">
                  <XIcon className="w-3 h-3" data-id="element-1409" />
                </button>
              </span>)}
          </div>}
      </div>

      {/* Table with Horizontal Scroll */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm" data-id="element-1410">
        <div className="overflow-x-auto" data-id="element-1411">
          <table className="w-full text-left text-sm font-inter" data-id="element-1412">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1413">
              <tr data-id="element-1414">
                <th className="px-6 py-3 font-semibold text-gray-900 sticky left-0 bg-gray-50 z-10 min-w-[280px]" data-id="element-1415">
                  Exhibitor
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[120px]" data-id="element-1416">
                  Stand
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[180px]" data-id="element-1417">
                  Assigned Events
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[140px]" data-id="element-1418">
                  Stand Type
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[140px]" data-id="element-1419">
                  Brand Profile
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[160px]" data-id="element-1420">
                  Exhibitor Portal
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 min-w-[140px]" data-id="element-1421">
                  Last Logged In
                </th>
                <th className="px-6 py-3 font-semibold text-gray-900 text-right sticky right-0 bg-gray-50 z-10 min-w-[80px]" data-id="element-1422">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-1423">
              {currentExhibitors.map(exhibitor => {
              const progressData = getProgressData(exhibitor.id);
              const hasChildren = isParentExhibitor(exhibitor.id);
              const childCount = getChildCount(exhibitor.id);
              const isChild = !!exhibitor.parentExhibitorId;
              return <tr key={exhibitor.id} className="group hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => handleRowClick(exhibitor)} data-id="element-1424">
                    <td className="px-6 py-4 sticky left-0 bg-white group-hover:bg-gray-50 z-10 transition-colors" data-id="element-1425">
                      <div className="flex items-center gap-3" data-id="element-1426">
                        {exhibitor.logo ? <img src={exhibitor.logo} alt={exhibitor.name} className="w-8 h-8 rounded object-cover border border-gray-200" data-id="element-1427" /> : <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500" data-id="element-1428">
                            {exhibitor.name.charAt(0)}
                          </div>}
                        <div className="flex items-center gap-2" data-id="element-1429">
                          <div data-id="element-1430">
                            <div className="font-medium text-gray-900 flex items-center gap-2" data-id="element-1431">
                              {isChild && <span className="text-gray-400 text-xs" data-id="element-1432">↳</span>}
                              {exhibitor.name}
                              {hasChildren && <Badge variant="default" size="sm" data-id="element-1433">
                                  <NetworkIcon className="w-3 h-3 mr-1" data-id="element-1434" />
                                  {childCount}
                                </Badge>}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-1435">
                              {exhibitor.headquarters || 'No HQ'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-xs bg-white group-hover:bg-gray-50 transition-colors" data-id="element-1436">
                      {exhibitor.standNumber}
                    </td>
                    <td className="px-6 py-4 bg-white group-hover:bg-gray-50 transition-colors" data-id="element-1437">
                      <div className="flex gap-1 flex-wrap" data-id="element-1438">
                        {exhibitor.events.map((event, idx) => <Badge key={`${event}-${idx}`} variant="default" size="sm" data-id="element-1439">
                            {event}
                          </Badge>)}
                      </div>
                    </td>
                    <td className="px-6 py-4 bg-white group-hover:bg-gray-50 transition-colors" data-id="element-1440">
                      <span className="text-sm text-gray-700" data-id="element-1441">
                        {exhibitor.standType}
                      </span>
                    </td>
                    <td className="px-6 py-4 bg-white group-hover:bg-gray-50 transition-colors" data-id="element-1442">
                      <div className="flex items-center gap-2" data-id="element-1443">
                        <div className="flex-1 bg-gray-200 rounded-full h-2" data-id="element-1444">
                          <div className="bg-blue-600 h-2 rounded-full transition-all" style={{
                        width: `${progressData.brandProfile}%`
                      }} data-id="element-1445" />
                        </div>
                        <span className="text-xs font-medium text-gray-700 w-8" data-id="element-1446">
                          {progressData.brandProfile}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 bg-white group-hover:bg-gray-50 transition-colors" data-id="element-1447">
                      <div className="flex items-center gap-2" data-id="element-1448">
                        <div className="flex-1 bg-gray-200 rounded-full h-2" data-id="element-1449">
                          <div className="bg-green-600 h-2 rounded-full transition-all" style={{
                        width: `${progressData.exhibitorPortal}%`
                      }} data-id="element-1450" />
                        </div>
                        <span className="text-xs font-medium text-gray-700 w-8" data-id="element-1451">
                          {progressData.exhibitorPortal}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 bg-white group-hover:bg-gray-50 transition-colors" data-id="element-1452">
                      <div className="flex items-center gap-1.5 text-xs text-gray-600" data-id="element-1453">
                        <ClockIcon className="w-3.5 h-3.5" data-id="element-1454" />
                        {progressData.lastLogin}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right sticky right-0 bg-white group-hover:bg-gray-50 transition-colors" onClick={e => e.stopPropagation()} style={{
                  zIndex: showActionsMenu === exhibitor.id ? 0 : 10
                }} data-id="element-1455">
                      <div className="relative" data-id="element-1456">
                        <button data-exhibitor-id={exhibitor.id} onClick={() => setShowActionsMenu(showActionsMenu === exhibitor.id ? null : exhibitor.id)} className="text-gray-400 hover:text-gray-600 relative z-20" data-id="element-1457">
                          <MoreHorizontalIcon className="w-5 h-5" data-id="element-1458" />
                        </button>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Actions Menu - Rendered outside table */}
        {showActionsMenu && <div className="fixed inset-0 z-50" onClick={() => setShowActionsMenu(null)} data-id="element-1459">
            {currentExhibitors.map(exhibitor => {
          if (showActionsMenu !== exhibitor.id) return null;
          return <div key={exhibitor.id} ref={actionsMenuRef} className="absolute w-48 bg-white rounded-lg shadow-lg border border-gray-200" style={{
            top: `${document.querySelector(`[data-exhibitor-id="${exhibitor.id}"]`)?.getBoundingClientRect().bottom || 0}px`,
            right: '2rem'
          }} onClick={e => e.stopPropagation()} data-id="element-1460">
                  <button onClick={() => {
              setShowActionsMenu(null);
              /* handleUpdateExhibitor(exhibitor) */
            }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100" data-id="element-1461">
                    <PencilIcon className="w-4 h-4" data-id="element-1462" />
                    Update Exhibitor
                  </button>
                  <button onClick={() => {
              setShowActionsMenu(null);
              /* handleSuspendExhibitor(exhibitor.id) */
            }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2" data-id="element-1463">
                    <BanIcon className="w-4 h-4" data-id="element-1464" />
                    Suspend Exhibitor
                  </button>
                </div>;
        })}
          </div>}

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 text-xs text-gray-500 flex justify-between items-center" data-id="element-1465">
          <span data-id="element-1466">
            Showing {startIndex + 1}-
            {Math.min(endIndex, filteredExhibitors.length)} of{' '}
            {filteredExhibitors.length} exhibitors
          </span>
          <div className="flex gap-2 items-center" data-id="element-1467">
            <span className="text-gray-600" data-id="element-1468">
              Page {currentPage} of {totalPages}
            </span>
            <button className="px-3 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled={currentPage === 1} onClick={handlePreviousPage} data-id="element-1469">
              Previous
            </button>
            <button className="px-3 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" disabled={currentPage === totalPages} onClick={handleNextPage} data-id="element-1470">
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Add Exhibitor Modal */}
      {showAddModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1471">
          <Card className="w-full max-w-2xl" data-id="element-1472">
            <div className="p-6 max-h-[85vh] overflow-y-auto" data-id="element-1473">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-1474">
                Add Exhibitor
              </h3>
              <div className="space-y-6" data-id="element-1475">
                {/* Company Name */}
                <div data-id="element-1476">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1477">
                    Company Name *
                  </label>
                  <Input type="text" placeholder="Acme Corporation" value={addName} onChange={e => setAddName(e.target.value)} data-id="element-1478" />
                </div>

                {/* Parent Exhibitor - Search Field */}
                <div data-id="element-1479">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1480">
                    Parent Exhibitor
                  </label>
                  <div className="relative" data-id="element-1481">
                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1482" />
                    <input type="text" placeholder={addParentExhibitor ? getParentExhibitorName(addParentExhibitor) : 'Search parent exhibitor...'} value={addParentSearchTerm} onChange={e => {
                  setAddParentSearchTerm(e.target.value);
                  setAddShowParentDropdown(e.target.value.length > 0);
                  if (e.target.value.length === 0) {
                    setAddParentExhibitor('');
                  }
                }} onFocus={() => setAddShowParentDropdown(addParentSearchTerm.length > 0 || !addParentExhibitor)} className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-1483" />
                    {addParentExhibitor && <button type="button" onClick={handleClearAddParent} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-1484">
                        <XIcon className="w-4 h-4" data-id="element-1485" />
                      </button>}
                    {addShowParentDropdown && filteredAddParentExhibitors.length > 0 && <>
                          <div className="fixed inset-0 z-10" onClick={() => setAddShowParentDropdown(false)} data-id="element-1486" />
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-id="element-1487">
                            {filteredAddParentExhibitors.map(ex => <button key={ex.id} type="button" onClick={() => handleSelectAddParent(ex.id)} className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0" data-id="element-1488">
                                <div className="text-sm font-medium text-gray-900" data-id="element-1489">
                                  {ex.name}
                                </div>
                                <div className="text-xs text-gray-500" data-id="element-1490">
                                  Stand {ex.standNumber}
                                </div>
                              </button>)}
                          </div>
                        </>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1" data-id="element-1491">
                    Select if this exhibitor is a child/subsidiary of another
                    exhibitor
                  </p>
                </div>

                {/* Stand Type Selection */}
                <div data-id="element-1492">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1493">
                    Stand Type *
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1494">
                    {STAND_TYPES.map(standType => {
                  const isSelected = addStandType === standType.type;
                  return <button key={standType.type} type="button" onClick={() => setAddStandType(standType.type)} className={`p-4 rounded-lg border-2 transition-all text-left h-24 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`} data-id="element-1495">
                          <div className="flex items-start gap-3 h-full" data-id="element-1496">
                            <div className={`flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-1497">
                              {standType.icon}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1498">
                              <div className={`font-semibold text-sm mb-1 ${isSelected ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-1499">
                                {standType.type}
                              </div>
                              <div className={`text-xs leading-tight ${isSelected ? 'text-blue-700' : 'text-gray-500'}`} data-id="element-1500">
                                {standType.description}
                              </div>
                            </div>
                          </div>
                        </button>;
                })}
                  </div>
                </div>

                {/* Stand Number */}
                <div data-id="element-1501">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1502">
                    Stand Number{' '}
                    {!addParentExhibitor && !isStandNumberDisabled(addStandType) && '*'}
                  </label>
                  <Input type="text" placeholder={isStandNumberDisabled(addStandType) ? 'Not applicable' : 'A123'} value={addStandNumber} onChange={e => setAddStandNumber(e.target.value)} disabled={isStandNumberDisabled(addStandType)} data-id="element-1503" />
                  {isStandNumberDisabled(addStandType) ? <p className="text-xs text-gray-500 mt-1" data-id="element-1504">
                      Stand number not required for Brand Only exhibitors
                    </p> : addParentExhibitor ? <p className="text-xs text-gray-500 mt-1" data-id="element-1505">
                      Optional for child exhibitors
                    </p> : null}
                </div>

                {/* Event Access */}
                <div data-id="element-1506">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1507">
                    Assigned Events *
                  </label>
                  <div className="text-xs text-gray-500 mb-3" data-id="element-1508">
                    Select which events this exhibitor will attend
                  </div>
                  <div className="space-y-2 border border-gray-200 rounded-lg p-3" data-id="element-1509">
                    {availableEvents.map(event => <label key={event.id} className="flex items-center gap-3 cursor-pointer" data-id="element-1510">
                        <input type="checkbox" checked={addSelectedEvents.includes(event.id)} onChange={() => toggleAddEventSelection(event.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1511" />
                        <span className="text-sm text-gray-900" data-id="element-1512">
                          {event.name}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Team Contacts Invitation */}
                <div className="border-t border-gray-200 pt-6" data-id="element-1513">
                  <div className="mb-4" data-id="element-1514">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-1515">
                      Invite Lead Team Contacts
                    </h4>
                    <p className="text-xs text-gray-500" data-id="element-1516">
                      Add existing users or create new invitations (these users
                      can manage their brand profile)
                    </p>
                  </div>

                  {/* Invited Contacts List */}
                  {addInvitedContacts.length > 0 && <div className="space-y-2 mb-4" data-id="element-1517">
                      {addInvitedContacts.map(contact => <div key={contact.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-1518">
                          <div className="flex-1" data-id="element-1519">
                            <div className="flex items-center gap-2" data-id="element-1520">
                              <span className="text-sm font-medium text-gray-900" data-id="element-1521">
                                {contact.name}
                              </span>
                              {contact.isExisting && <Badge variant="success" size="sm" data-id="element-1522">
                                  Existing User
                                </Badge>}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-1523">
                              {contact.email}
                              {contact.role && ` • ${contact.role}`}
                            </div>
                          </div>
                          <button type="button" onClick={() => handleRemoveContact(contact.id)} className="p-1 text-gray-400 hover:text-red-600 transition-colors" data-id="element-1524">
                            <XIcon className="w-4 h-4" data-id="element-1525" />
                          </button>
                        </div>)}
                    </div>}

                  {/* User Lookup */}
                  <div className="space-y-3" data-id="element-1526">
                    <div className="relative" data-id="element-1527">
                      <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1528" />
                      <input type="text" placeholder="Search existing users..." value={addUserSearchTerm} onChange={e => {
                    setAddUserSearchTerm(e.target.value);
                    setAddShowUserLookup(e.target.value.length > 0);
                  }} onFocus={() => setAddShowUserLookup(addUserSearchTerm.length > 0)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-1529" />
                      {addShowUserLookup && filteredAppUsers.length > 0 && <>
                          <div className="fixed inset-0 z-10" onClick={() => setAddShowUserLookup(false)} data-id="element-1530" />
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-id="element-1531">
                            {filteredAppUsers.map(user => <button key={user.id} type="button" onClick={() => handleAddExistingUser(user)} className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0" data-id="element-1532">
                                <div className="text-sm font-medium text-gray-900" data-id="element-1533">
                                  {user.name}
                                </div>
                                <div className="text-xs text-gray-500" data-id="element-1534">
                                  {user.email} • {user.role}
                                </div>
                              </button>)}
                          </div>
                        </>}
                    </div>

                    {/* Add New Contact */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-1535">
                      <div className="text-xs font-medium text-blue-900 mb-3" data-id="element-1536">
                        Or create new invitation
                      </div>
                      <div className="space-y-3" data-id="element-1537">
                        <div className="grid grid-cols-2 gap-3" data-id="element-1538">
                          <div data-id="element-1539">
                            <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1540">
                              Name *
                            </label>
                            <Input type="text" placeholder="John Doe" value={addNewContactName} onChange={e => setAddNewContactName(e.target.value)} className="text-sm" data-id="element-1541" />
                          </div>
                          <div data-id="element-1542">
                            <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1543">
                              Email *
                            </label>
                            <Input type="email" placeholder="john@example.com" value={addNewContactEmail} onChange={e => setAddNewContactEmail(e.target.value)} className="text-sm" data-id="element-1544" />
                          </div>
                        </div>
                        <div data-id="element-1545">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1546">
                            Role
                          </label>
                          <Input type="text" placeholder="e.g. Sales Manager" value={addNewContactRole} onChange={e => setAddNewContactRole(e.target.value)} className="text-sm" data-id="element-1547" />
                        </div>
                        <Button variant="outline" size="sm" onClick={handleAddNewContact} disabled={!addNewContactName || !addNewContactEmail} className="w-full" data-id="element-1548">
                          <PlusIcon className="w-3.5 h-3.5 mr-1.5" data-id="element-1549" />
                          Add Contact
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-8" data-id="element-1550">
                <Button variant="outline" onClick={() => setShowAddModal(false)} className="flex-1" data-id="element-1551">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleAddExhibitor} disabled={!addName || !addParentExhibitor && !isStandNumberDisabled(addStandType) && !addStandNumber || addSelectedEvents.length === 0} data-id="element-1552">
                  <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-1553" />
                  Add Exhibitor
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* Update Exhibitor Modal */}
      {showUpdateModal && exhibitorToUpdate && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1554">
          <Card className="w-full max-w-2xl" data-id="element-1555">
            <div className="p-6 max-h-[85vh] overflow-y-auto" data-id="element-1556">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-1557">
                Update Exhibitor
              </h3>
              <div className="space-y-6" data-id="element-1558">
                <div data-id="element-1559">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1560">
                    Company Name *
                  </label>
                  <Input type="text" value={updateName} onChange={e => setUpdateName(e.target.value)} data-id="element-1561" />
                </div>

                {/* Parent Exhibitor - Search Field */}
                <div data-id="element-1562">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1563">
                    Parent Exhibitor
                  </label>
                  <div className="relative" data-id="element-1564">
                    <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1565" />
                    <input type="text" placeholder={updateParentExhibitor ? getParentExhibitorName(updateParentExhibitor) : 'Search parent exhibitor...'} value={updateParentSearchTerm} onChange={e => {
                  setUpdateParentSearchTerm(e.target.value);
                  setUpdateShowParentDropdown(e.target.value.length > 0);
                  if (e.target.value.length === 0) {
                    setUpdateParentExhibitor('');
                  }
                }} onFocus={() => setUpdateShowParentDropdown(updateParentSearchTerm.length > 0 || !updateParentExhibitor)} className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" data-id="element-1566" />
                    {updateParentExhibitor && <button type="button" onClick={handleClearUpdateParent} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-1567">
                        <XIcon className="w-4 h-4" data-id="element-1568" />
                      </button>}
                    {updateShowParentDropdown && filteredUpdateParentExhibitors.length > 0 && <>
                          <div className="fixed inset-0 z-10" onClick={() => setUpdateShowParentDropdown(false)} data-id="element-1569" />
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-id="element-1570">
                            {filteredUpdateParentExhibitors.map(ex => <button key={ex.id} type="button" onClick={() => handleSelectUpdateParent(ex.id)} className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0" data-id="element-1571">
                                <div className="text-sm font-medium text-gray-900" data-id="element-1572">
                                  {ex.name}
                                </div>
                                <div className="text-xs text-gray-500" data-id="element-1573">
                                  Stand {ex.standNumber}
                                </div>
                              </button>)}
                          </div>
                        </>}
                  </div>
                </div>

                <div data-id="element-1574">
                  <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1575">
                    Stand Type *
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1576">
                    {STAND_TYPES.map(standType => {
                  const isSelected = updateStandType === standType.type;
                  return <button key={standType.type} type="button" onClick={() => setUpdateStandType(standType.type)} className={`p-4 rounded-lg border-2 transition-all text-left h-24 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`} data-id="element-1577">
                          <div className="flex items-start gap-3 h-full" data-id="element-1578">
                            <div className={`flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-1579">
                              {standType.icon}
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-1580">
                              <div className={`font-semibold text-sm mb-1 ${isSelected ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-1581">
                                {standType.type}
                              </div>
                              <div className={`text-xs leading-tight ${isSelected ? 'text-blue-700' : 'text-gray-500'}`} data-id="element-1582">
                                {standType.description}
                              </div>
                            </div>
                          </div>
                        </button>;
                })}
                  </div>
                </div>

                <div data-id="element-1583">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1584">
                    Stand Number{' '}
                    {!updateParentExhibitor && !isStandNumberDisabled(updateStandType) && '*'}
                  </label>
                  <Input type="text" placeholder={isStandNumberDisabled(updateStandType) ? 'Not applicable' : 'A123'} value={updateStandNumber} onChange={e => setUpdateStandNumber(e.target.value)} disabled={isStandNumberDisabled(updateStandType)} data-id="element-1585" />
                  {isStandNumberDisabled(updateStandType) && <p className="text-xs text-gray-500 mt-1" data-id="element-1586">
                      Stand number not required for Brand Only exhibitors
                    </p>}
                </div>

                <div data-id="element-1587">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1588">
                    Assigned Events *
                  </label>
                  <div className="space-y-2 border border-gray-200 rounded-lg p-3" data-id="element-1589">
                    {availableEvents.map(event => <label key={event.id} className="flex items-center gap-3 cursor-pointer" data-id="element-1590">
                        <input type="checkbox" checked={updateSelectedEvents.includes(event.id)} onChange={() => toggleUpdateEventSelection(event.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1591" />
                        <span className="text-sm text-gray-900" data-id="element-1592">
                          {event.name}
                        </span>
                      </label>)}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-8" data-id="element-1593">
                <Button variant="outline" onClick={() => {
              setShowUpdateModal(false);
              setExhibitorToUpdate(null);
            }} className="flex-1" data-id="element-1594">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleSaveUpdate} disabled={!updateName || !updateParentExhibitor && !isStandNumberDisabled(updateStandType) && !updateStandNumber || updateSelectedEvents.length === 0} data-id="element-1595">
                  <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-1596" />
                  Save Changes
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* Invite User Modal */}
      {showInviteUserModal && selectedExhibitorForInvite && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1597">
          <Card className="w-full max-w-md" data-id="element-1598">
            <div className="p-6" data-id="element-1599">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1600">
                Invite Team Member
              </h3>
              <div className="space-y-4" data-id="element-1601">
                <div className="p-3 bg-gray-50 rounded-lg" data-id="element-1602">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1603">
                    {selectedExhibitorForInvite.name}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-1604">
                    Stand {selectedExhibitorForInvite.standNumber}
                  </div>
                </div>
                <div data-id="element-1605">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1606">
                    Name *
                  </label>
                  <Input type="text" placeholder="John Doe" value={inviteUserName} onChange={e => setInviteUserName(e.target.value)} data-id="element-1607" />
                </div>
                <div data-id="element-1608">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1609">
                    Email *
                  </label>
                  <Input type="email" placeholder="john@company.com" value={inviteUserEmail} onChange={e => setInviteUserEmail(e.target.value)} data-id="element-1610" />
                </div>
                <div data-id="element-1611">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1612">
                    Role
                  </label>
                  <Input type="text" placeholder="e.g. Marketing Manager" value={inviteUserRole} onChange={e => setInviteUserRole(e.target.value)} data-id="element-1613" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer" data-id="element-1614">
                  <input type="checkbox" checked={inviteAsPrimary} onChange={e => setInviteAsPrimary(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1615" />
                  <span className="text-sm text-gray-700" data-id="element-1616">
                    Set as primary contact
                  </span>
                </label>
              </div>
              <div className="flex gap-3 mt-6" data-id="element-1617">
                <Button variant="outline" onClick={() => {
              setShowInviteUserModal(false);
              setSelectedExhibitorForInvite(null);
            }} className="flex-1" data-id="element-1618">
                  Cancel
                </Button>
                <Button variant="primary" className="flex-1" onClick={handleInviteUser} disabled={!inviteUserName || !inviteUserEmail} data-id="element-1619">
                  <MailIcon className="w-4 h-4 mr-2" data-id="element-1620" />
                  Send Invitation
                </Button>
              </div>
            </div>
          </Card>
        </div>}

      {/* View Team Modal */}
      {showTeamModal && selectedExhibitorForTeam && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1621">
          <Card className="w-full max-w-lg" data-id="element-1622">
            <div className="p-6" data-id="element-1623">
              <div className="flex items-center justify-between mb-6" data-id="element-1624">
                <div data-id="element-1625">
                  <h3 className="text-lg font-bold text-gray-900" data-id="element-1626">
                    Team Members
                  </h3>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-1627">
                    {selectedExhibitorForTeam.name}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => {
              setShowTeamModal(false);
              handleOpenInviteUser(selectedExhibitorForTeam);
            }} data-id="element-1628">
                  <UserPlusIcon className="w-4 h-4 mr-2" data-id="element-1629" />
                  Invite
                </Button>
              </div>
              <div className="space-y-3" data-id="element-1630">
                {getExhibitorUsers(selectedExhibitorForTeam.id).map(user => <div key={user.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-1631">
                    <div className="flex items-start justify-between" data-id="element-1632">
                      <div className="flex-1" data-id="element-1633">
                        <div className="flex items-center gap-2" data-id="element-1634">
                          <div className="text-sm font-medium text-gray-900" data-id="element-1635">
                            {user.name}
                          </div>
                          {user.isPrimary && <Badge variant="success" size="sm" data-id="element-1636">
                              Primary
                            </Badge>}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-1637">
                          {user.email}
                        </div>
                        {user.role && <div className="text-xs text-gray-600 mt-1" data-id="element-1638">
                            {user.role}
                          </div>}
                      </div>
                    </div>
                  </div>)}
              </div>
              <div className="mt-6" data-id="element-1639">
                <Button variant="outline" onClick={() => {
              setShowTeamModal(false);
              setSelectedExhibitorForTeam(null);
            }} className="w-full" data-id="element-1640">
                  Close
                </Button>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}